// src/components/eventos/views/EventosRelatorios.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import EventoRevisao from '../EventoRevisao';
import EventoRelatorio from '../EventoRelatorio';

/* ---- Helpers ---- */
const formatarData = (dataString) => {
    if (!dataString) return 'Data não informada';
    try {
        const [ano, mes, dia] = dataString.split('T')[0].split('-');
        return `${dia}/${mes}/${ano}`;
    } catch { return 'Data inválida'; }
};

/* ---- SVG Icons ---- */
const ArrowLeftIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const FileIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
    </svg>
);
const ChartIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
);
const ListIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
);
const EyeIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);
const CalendarIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const CheckIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const LogoutIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);
const ChevronIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const STATUS_STYLE = {
    em_andamento: { label: 'EM ANDAMENTO', color: '#2CCB90', bg: 'rgba(44,203,144,0.1)', border: 'rgba(44,203,144,0.25)' },
    concluido:    { label: 'CONCLUÍDO',    color: '#64748b', bg: 'rgba(100,116,139,0.08)', border: 'rgba(100,116,139,0.2)' },
    agendado:     { label: 'AGENDADO',     color: '#2563eb', bg: 'rgba(37,99,235,0.08)', border: 'rgba(37,99,235,0.2)' },
};

const SearchIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);
const XIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const EventosRelatorios = ({ supabase, session, clienteData, onLogout }) => {
    const navigate = useNavigate();
    const toast = useToast();

    const [eventos, setEventos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [tabAtiva, setTabAtiva] = useState(0);
    const [busca, setBusca] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('todos');
    const [eventoRevisaoSelecionado, setEventoRevisaoSelecionado] = useState(null);
    const [eventoRelatorioSelecionado, setEventoRelatorioSelecionado] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);

    const nomeUsuario = session?.user?.user_metadata?.nome || 'Usuário';
    const iniciais = nomeUsuario.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

    const handleLogout = async () => { try { await onLogout(); } catch (e) { console.error(e); } };

    const fetchEventos = useCallback(async () => {
        setIsLoading(true);
        try {
            const clienteId = session.user.user_metadata?.cliente_id;
            let query = supabase.from('eventos').select('*').order('data', { ascending: false });
            if (clienteId) query = query.eq('cliente_id', clienteId);
            const { data, error } = await query;
            if (error) throw error;
            setEventos(data || []);
        } catch (error) {
            console.error('Erro ao buscar eventos:', error);
            toast({ title: 'Erro ao carregar eventos', description: error.message, status: 'error', duration: 5000, isClosable: true });
        } finally {
            setIsLoading(false);
        }
    }, [supabase, session.user.user_metadata, toast]);

    useEffect(() => { fetchEventos(); }, [fetchEventos]);

    React.useEffect(() => {
        if (!menuOpen) return;
        const close = () => setMenuOpen(false);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [menuOpen]);

    const filtrarEventos = (lista) => lista.filter(e => {
        const matchBusca = !busca.trim() || e.titulo?.toLowerCase().includes(busca.toLowerCase());
        const matchStatus = filtroStatus === 'todos' || e.status === filtroStatus;
        return matchBusca && matchStatus;
    });

    const eventosCadastrados = filtrarEventos(eventos);
    const eventosConcluidos = filtrarEventos(eventos.filter(e => e.status === 'concluido'));

    const renderCard = (evento, tipo) => {
        const st = STATUS_STYLE[evento.status] || STATUS_STYLE.agendado;
        const isRelatorio = tipo === 'relatorio';
        return (
            <div key={evento.id} className="er-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>{evento.titulo}</h3>
                            <span style={{ padding: '3px 10px', borderRadius: '100px', background: st.bg, border: `1px solid ${st.border}`, color: st.color, fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em' }}>
                                {st.label}
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '8px', background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.15)', color: '#7c3aed', fontSize: '12px', fontWeight: 500 }}>
                                <CalendarIcon /> {formatarData(evento.data)}
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '8px', background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.15)', color: '#2563eb', fontSize: '12px', fontWeight: 500 }}>
                                <CheckIcon /> {evento.tarefas?.length || 0} tarefas
                            </span>
                        </div>
                        {evento.descricao && (
                            <p style={{ margin: 0, fontSize: '13px', color: '#64748b', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                {evento.descricao}
                            </p>
                        )}
                    </div>
                    <button
                        className={`er-action-btn ${isRelatorio ? 'purple' : 'blue'}`}
                        onClick={() => isRelatorio ? setEventoRelatorioSelecionado(evento) : setEventoRevisaoSelecionado(evento)}
                    >
                        <EyeIcon /> {isRelatorio ? 'Ver Relatório' : 'Ver Detalhes'}
                    </button>
                </div>
            </div>
        );
    };

    const renderEmpty = (tipo) => (
        <div style={{ textAlign: 'center', padding: '60px 24px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#cbd5e1' }}>
                {tipo === 'relatorio' ? <ChartIcon /> : <FileIcon />}
            </div>
            <p style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: '#64748b' }}>
                {tipo === 'relatorio' ? 'Nenhum relatório disponível' : 'Nenhum evento cadastrado'}
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>
                {tipo === 'relatorio'
                    ? 'Relatórios de eventos concluídos aparecerão aqui'
                    : 'Eventos cadastrados aparecerão aqui para você revisar a programação'}
            </p>
            {tipo !== 'relatorio' && (
                <button
                    onClick={() => navigate('/eventos/cadastro')}
                    style={{ marginTop: '20px', padding: '10px 24px', borderRadius: '10px', background: 'linear-gradient(135deg, #2CCB90, #2DB8E1)', border: 'none', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                    Criar Evento
                </button>
            )}
        </div>
    );

    return (
        <>
            <style>{`
                @keyframes er-spin { to { transform: rotate(360deg); } }

                .er-tab-btn { display:inline-flex; align-items:center; gap:8px; padding:9px 18px; border-radius:10px; font-size:13px; font-weight:600; border:1px solid transparent; cursor:pointer; font-family:inherit; transition:all 0.2s; white-space:nowrap; }
                .er-tab-btn.active { background:rgba(44,203,144,0.1); border-color:rgba(44,203,144,0.25); color:#2CCB90; }
                .er-tab-btn.inactive { background:#f8fafc; border-color:#e2e8f0; color:#64748b; }
                .er-tab-btn.inactive:hover { background:#f0fdf8; color:#1e293b; }

                .er-card { background:#ffffff; border:1px solid #e2e8f0; border-radius:14px; padding:20px; transition:all 0.2s; box-shadow:0 1px 3px rgba(0,0,0,0.05); }
                .er-card:hover { border-color:rgba(124,58,237,0.2); box-shadow:0 4px 16px rgba(0,0,0,0.08); transform:translateY(-2px); }

                .er-action-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border-radius:10px; font-size:13px; font-weight:600; border:1px solid transparent; cursor:pointer; font-family:inherit; transition:all 0.2s; flex-shrink:0; }
                .er-action-btn.blue { background:rgba(37,99,235,0.08); border-color:rgba(37,99,235,0.2); color:#2563eb; }
                .er-action-btn.blue:hover { background:rgba(37,99,235,0.14); }
                .er-action-btn.purple { background:rgba(124,58,237,0.08); border-color:rgba(124,58,237,0.2); color:#7c3aed; }
                .er-action-btn.purple:hover { background:rgba(124,58,237,0.14); }

                .er-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; }
                @media (max-width:900px) { .er-grid { grid-template-columns:1fr; } }

                .er-menu-item { display:flex; align-items:center; gap:10px; width:100%; padding:10px 14px; border-radius:10px; font-size:13px; font-weight:500; color:#64748b; background:none; border:none; cursor:pointer; font-family:inherit; transition:all 0.15s; text-align:left; }
                .er-menu-item:hover { background:#f8fafc; color:#1e293b; }
                .er-menu-item.danger { color:#ef4444; }
                .er-menu-item.danger:hover { background:rgba(239,68,68,0.06); }

                @media (max-width:640px) { .er-nav-name { display:none; } }
            `}</style>

            <div style={{ minHeight: '100vh', background: '#f0faf6', fontFamily: "'Inter', -apple-system, sans-serif" }}>

                {/* NAVBAR */}
                <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', background: '#ffffff', borderRadius: '10px', padding: '6px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
                                <img src="/logo-horizontal.png" alt="Evlo" style={{ height: '26px', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                            </div>
                            <button
                                onClick={() => navigate('/eventos')}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f0fdf8'}
                                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                            >
                                <ArrowLeftIcon /> Voltar aos Eventos
                            </button>
                            {clienteData && (
                                <span style={{ padding: '4px 12px', borderRadius: '100px', background: 'rgba(44,203,144,0.12)', border: '1px solid rgba(44,203,144,0.2)', color: '#2CCB90', fontSize: '11px', fontWeight: 700 }}>
                                    {clienteData.nome}
                                </span>
                            )}
                        </div>

                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 12px', borderRadius: '12px', background: menuOpen ? '#f8fafc' : '#ffffff', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit' }}
                            >
                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #2CCB90, #2DB8E1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                                    {iniciais}
                                </div>
                                <span className="er-nav-name" style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{nomeUsuario}</span>
                                <span style={{ color: '#94a3b8', transition: 'transform 0.2s', transform: menuOpen ? 'rotate(180deg)' : 'none' }}><ChevronIcon /></span>
                            </button>

                            {menuOpen && (
                                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: '190px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '14px', boxShadow: '0 16px 48px rgba(0,0,0,0.12)', padding: '8px', zIndex: 100 }}>
                                    <button className="er-menu-item" onClick={() => { navigate('/perfil'); setMenuOpen(false); }}>Meu Perfil</button>
                                    <div style={{ height: '1px', background: '#e2e8f0', margin: '6px 0' }} />
                                    <button className="er-menu-item danger" onClick={handleLogout}><LogoutIcon /> Sair</button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* CONTEÚDO */}
                <main style={{ position: 'relative', zIndex: 1, maxWidth: '1280px', margin: '0 auto', padding: '32px 24px 80px' }}>

                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(124,58,237,0.12), rgba(45,184,225,0.12))', border: '1px solid rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed' }}>
                            <FileIcon />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 'clamp(20px,3vw,28px)', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>Relatórios e Documentos</h1>
                            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                                Visualize a programação e acesse relatórios detalhados de eventos concluídos
                            </p>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', marginTop: '20px', flexWrap: 'wrap' }}>
                        <button className={`er-tab-btn ${tabAtiva === 0 ? 'active' : 'inactive'}`} onClick={() => setTabAtiva(0)}>
                            <ListIcon /> Eventos Cadastrados ({eventosCadastrados.length})
                        </button>
                        <button className={`er-tab-btn ${tabAtiva === 1 ? 'active' : 'inactive'}`} onClick={() => setTabAtiva(1)}>
                            <ChartIcon /> Relatórios de Métricas ({eventosConcluidos.length})
                        </button>
                    </div>

                    {/* Barra de busca + filtros */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: '1 1 220px' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}>
                                <SearchIcon />
                            </span>
                            <input
                                value={busca}
                                onChange={e => setBusca(e.target.value)}
                                placeholder="Buscar por título..."
                                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 36px 9px 36px', borderRadius: '10px', background: '#ffffff', border: '1px solid #e2e8f0', color: '#1e293b', fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
                                onFocus={e => { e.target.style.borderColor = '#2CCB90'; e.target.style.background = '#f0fdf8'; }}
                                onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#ffffff'; }}
                            />
                            {busca && (
                                <button onClick={() => setBusca('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px', display: 'flex' }}>
                                    <XIcon />
                                </button>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {[
                                { value: 'todos', label: 'Todos' },
                                { value: 'agendado', label: 'Agendado' },
                                { value: 'em_andamento', label: 'Em Andamento' },
                                { value: 'concluido', label: 'Concluído' },
                            ].map(f => (
                                <button key={f.value} onClick={() => setFiltroStatus(f.value)}
                                    style={{ padding: '7px 14px', borderRadius: '9px', fontSize: '12px', fontWeight: 600, border: '1px solid', fontFamily: 'inherit', cursor: 'pointer', transition: 'all 0.15s',
                                        background: filtroStatus === f.value ? 'rgba(44,203,144,0.1)' : '#ffffff',
                                        borderColor: filtroStatus === f.value ? 'rgba(44,203,144,0.3)' : '#e2e8f0',
                                        color: filtroStatus === f.value ? '#2CCB90' : '#64748b',
                                    }}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Conteúdo das abas */}
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '3px solid rgba(44,203,144,0.2)', borderTopColor: '#2CCB90', animation: 'er-spin 0.8s linear infinite' }} />
                        </div>
                    ) : tabAtiva === 0 ? (
                        eventosCadastrados.length === 0 ? renderEmpty('cadastro') : (
                            <div className="er-grid">
                                {eventosCadastrados.map(e => renderCard(e, 'revisao'))}
                            </div>
                        )
                    ) : (
                        eventosConcluidos.length === 0 ? renderEmpty('relatorio') : (
                            <div className="er-grid">
                                {eventosConcluidos.map(e => renderCard(e, 'relatorio'))}
                            </div>
                        )
                    )}
                </main>
            </div>

            {/* Modais Chakra */}
            <EventoRevisao
                isOpen={!!eventoRevisaoSelecionado}
                onClose={() => setEventoRevisaoSelecionado(null)}
                evento={eventoRevisaoSelecionado}
            />
            <EventoRelatorio
                isOpen={!!eventoRelatorioSelecionado}
                onClose={() => setEventoRelatorioSelecionado(null)}
                evento={eventoRelatorioSelecionado}
            />
        </>
    );
};

export default EventosRelatorios;
